(() => {
  const config = window.freeGiftConfig || {};

  const REQUIRED_QTY = Number(config.requiredQty || 6);

  let syncPromise = null;
  let syncAgain = false;
  let checkoutRedirecting = false;
  let checkoutLocked = false;

  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    'form[action="/checkout"] [type="submit"]',
    ".cart__checkout",
    'button[id*="checkout"]',
    'button[class*="checkout"]',
  ].join(",");

  let runtimeGiftVariantId = Number(window.freeGiftConfig?.variantId || 0);

  function getGiftVariantId(cart) {
    const bundleItem = cart?.items?.find(
      (item) => item.properties?._gift_variant_id,
    );

    if (bundleItem) {
      runtimeGiftVariantId = Number(bundleItem.properties._gift_variant_id);
      return runtimeGiftVariantId;
    }

    const existingGift = cart?.items?.find(
      (item) => item.properties?._free_gift === "true",
    );

    if (existingGift) {
      runtimeGiftVariantId = Number(existingGift.id);
      return runtimeGiftVariantId;
    }

    const giftElement = document.querySelector(
      ".gift-item__with-product[data-gift-id]",
    );
    const sectionGiftId = Number(giftElement?.dataset.giftId || 0);

    if (sectionGiftId) {
      runtimeGiftVariantId = sectionGiftId;
      return sectionGiftId;
    }

    return runtimeGiftVariantId;
  }

  /* =============================================
     SHADOW DOM HELPERS
  ============================================= */

  function deepFindCheckoutButtons(root = document) {
    let buttons = [];

    try {
      buttons.push(...root.querySelectorAll(CHECKOUT_SELECTOR));
    } catch (error) {
      console.warn("Checkout selector error:", error);
    }

    root.querySelectorAll("*").forEach((element) => {
      if (element.shadowRoot) {
        buttons.push(...deepFindCheckoutButtons(element.shadowRoot));
      }
    });

    return [...new Set(buttons)];
  }

  /* =============================================
     CHECKOUT BUTTON STATE
  ============================================= */

  function applyCheckoutState() {
    const buttons = deepFindCheckoutButtons();

    buttons.forEach((button) => {
      if (checkoutLocked) {
        if (button.dataset.freeGiftLocked !== "true") {
          button.dataset.freeGiftLocked = "true";

          if ("disabled" in button) {
            button.dataset.freeGiftWasDisabled = button.disabled
              ? "true"
              : "false";

            button.disabled = true;
          }

          button.setAttribute("aria-disabled", "true");
          button.classList.add("free-gift-checking");
        }
      } else {
        if (button.dataset.freeGiftLocked !== "true") return;

        if (
          "disabled" in button &&
          button.dataset.freeGiftWasDisabled !== "true"
        ) {
          button.disabled = false;
        }

        button.removeAttribute("aria-disabled");
        button.classList.remove("free-gift-checking");

        delete button.dataset.freeGiftLocked;
        delete button.dataset.freeGiftWasDisabled;
      }
    });
  }

  function setCheckoutLocked(locked) {
    checkoutLocked = locked;
    applyCheckoutState();
  }

  /* =============================================
     CART API
  ============================================= */

  async function getCart() {
    const response = await fetch("/cart.js", {
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("Unable to fetch cart.");
    }

    return response.json();
  }

  async function removeLine(key) {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: key,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error("Unable to remove free gift.");
    }

    return response.json();
  }

  async function addGift(giftVariantId) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: giftVariantId,
            quantity: 1,
            properties: {
              _free_gift: "true",
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error("Unable to add free gift.");
    }

    return response.json();
  }

  /* =============================================
     CART HELPERS
  ============================================= */

  function isGift(item, giftVariantId = 0) {
    return (
      item.properties?._free_gift === "true" ||
      (giftVariantId && Number(item.id) === Number(giftVariantId))
    );
  }
  function getQualifyingQuantity(cart, giftVariantId = 0) {
    const taggedItems = cart.items.filter(
      (item) =>
        !isGift(item, giftVariantId) &&
        item.properties?._gift_variant_id &&
        (!giftVariantId ||
          Number(item.properties._gift_variant_id) === Number(giftVariantId)),
    );

    if (taggedItems.length > 0) {
      return taggedItems.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0,
      );
    }

    const hasLegacyGiftSource =
      Number(config.variantId || 0) > 0 ||
      Number(
        document.querySelector(".gift-item__with-product[data-gift-id]")
          ?.dataset.giftId || 0,
      ) > 0;

    if (!hasLegacyGiftSource) return 0;

    return cart.items.reduce((total, item) => {
      if (isGift(item, giftVariantId)) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  function getGiftLines(cart) {
    return cart.items.filter(isGift);
  }

  /* =============================================
     PUB SUB REFRESH
  ============================================= */

  function publishCartUpdate(cart) {
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined" &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "free-gift-manager",
        cartData: cart,
      });
    }

    document.dispatchEvent(
      new CustomEvent("freegift:updated", {
        detail: {
          cart,
        },
      }),
    );
  }

  /* =============================================
     RECONCILE GIFT
  ============================================= */

  async function reconcileGift() {
    let cart = await getCart();
    const giftVariantId = getGiftVariantId(cart);

    const qualifyingQty = getQualifyingQuantity(cart, giftVariantId);
    const giftLines = cart.items.filter((item) => isGift(item, giftVariantId));

    const validGift =
      giftLines.length === 1 &&
      Number(giftLines[0].quantity) === 1 &&
      giftLines[0].properties?._free_gift === "true";

    /*
     * Determine whether the cart actually needs modification.
     *
     * >= 6 = exactly one marked gift
     * < 6  = zero gifts
     */
    const needsGiftAdded = qualifyingQty >= REQUIRED_QTY && !validGift;

    const needsGiftRemoved =
      qualifyingQty < REQUIRED_QTY && giftLines.length > 0;

    const needsCorrection = needsGiftAdded || needsGiftRemoved;

    /*
     * IMPORTANT:
     * Only disable checkout if the cart is actually inconsistent.
     *
     * This removes the repeated 5-second blinking.
     */
    if (needsCorrection) {
      setCheckoutLocked(true);
    }

    let changed = false;

    try {
      /* ===============================
         QUALIFIES
      =============================== */

      if (qualifyingQty >= REQUIRED_QTY) {
        const validGift =
          giftLines.length === 1 &&
          giftLines[0].quantity === 1 &&
          giftLines[0].properties?._free_gift === "true";

        if (!validGift) {
          if (!giftVariantId) {
            console.warn("Free Gift Manager: No gift variant ID found.");

            return {
              cart,
              changed: false,
            };
          }

          for (const gift of giftLines) {
            await removeLine(gift.key);
          }

          await addGift(giftVariantId);

          changed = true;
        }
      } else if (giftLines.length > 0) {
        /* ===============================
         DOES NOT QUALIFY
      =============================== */
        for (const gift of giftLines) {
          await removeLine(gift.key);
        }

        changed = true;
      }

      if (changed) {
        cart = await getCart();
      }

      return {
        cart,
        changed,
      };
    } finally {
      /*
       * Once reconciliation finishes, cart is safe again.
       */
      if (needsCorrection) {
        setCheckoutLocked(false);
      }
    }
  }

  /* =============================================
     SYNC QUEUE
  ============================================= */

  function sync() {
    /*
     * If another cart change happens while syncing,
     * don't throw it away.
     */
    if (syncPromise) {
      syncAgain = true;
      return syncPromise;
    }

    syncPromise = (async () => {
      let finalCart = null;

      do {
        syncAgain = false;

        const result = await reconcileGift();

        finalCart = result.cart;

        if (result.changed) {
          publishCartUpdate(result.cart);
        }
      } while (syncAgain);

      return finalCart;
    })()
      .catch((error) => {
        console.error("Free Gift Manager:", error);
        throw error;
      })
      .finally(() => {
        syncPromise = null;
      });

    return syncPromise;
  }

  window.FreeGiftManager = {
    sync,
  };

  /* =============================================
     CHECKOUT GUARD
  ============================================= */

  async function checkoutGuard(event) {
    if (checkoutRedirecting) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    checkoutRedirecting = true;

    /*
     * Disable only because the customer is
     * actively trying to checkout.
     */
    setCheckoutLocked(true);

    try {
      /*
       * Final real Shopify cart validation.
       */
      await sync();

      window.location.assign("/checkout");
    } catch (error) {
      console.error("Checkout gift validation failed:", error);

      checkoutRedirecting = false;
      setCheckoutLocked(false);

      alert(
        "We couldn't finish updating your cart. Please try checkout again.",
      );
    }
  }

  /* =============================================
     ATTACH CHECKOUT GUARDS
  ============================================= */

  function attachCheckoutGuards() {
    const buttons = deepFindCheckoutButtons();

    buttons.forEach((button) => {
      if (button.dataset.freeGiftGuard === "true") {
        return;
      }

      button.dataset.freeGiftGuard = "true";

      button.addEventListener("click", checkoutGuard, {
        capture: true,
      });
    });

    /*
     * Reapply state in case cart drawer HTML
     * was replaced by AJAX.
     */
    applyCheckoutState();
  }

  /* =============================================
     INITIALIZE
  ============================================= */

  function init() {
    /*
     * Initial cart correction.
     */
    sync().catch(() => {});

    /*
     * AJAX cart drawers can replace their entire
     * checkout button.
     */
    const observer = new MutationObserver(() => {
      attachCheckoutGuards();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    /*
     * Shadow DOM fallback.
     *
     * This does NOT disable anything every second.
     * It simply makes sure newly-created drawer
     * checkout buttons receive the guard.
     */
    setInterval(() => {
      attachCheckoutGuards();
    }, 800);

    /*
     * Cart-state fallback.
     *
     * Unlike your current script this doesn't
     * disable checkout when nothing is wrong.
     */
    setInterval(() => {
      sync().catch(() => {});
    }, 5000);

    attachCheckoutGuards();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
